import { COMMUNIO_BRAND_HUE } from '@communio/ui-tokens';
import { readThemeCookie } from '@/platform/cookies/consent';
import type { SiteAppearancePublic } from '@/platform/siteappearance/types';

export type ThemeBg = 'light' | 'dim' | 'lights-out';

export type ThemeState = {
  fontSize: string;
  primaryHue: string;
  bg: ThemeBg;
};

export const THEME_DEFAULT: ThemeState = {
  fontSize: '16px',
  primaryHue: COMMUNIO_BRAND_HUE,
  bg: 'light',
};

export const BG_PRESETS: Record<ThemeBg, { dark: string; light: string; white: string }> = {
  light: { dark: '17%', light: '95%', white: '100%' },
  dim: { dark: '95%', light: '15%', white: '20%' },
  'lights-out': { dark: '95%', light: '0%', white: '10%' },
};

/** Link lightness per background — darker links on light UI, lighter on dark UI. */
export const LINK_PRESETS: Record<ThemeBg, { link: string; hover: string }> = {
  light: { link: '42%', hover: '36%' },
  dim: { link: '78%', hover: '85%' },
  'lights-out': { link: '82%', hover: '88%' },
};

export const THEME_COLOR_SWATCHES = [
  { hue: COMMUNIO_BRAND_HUE },
  { hue: '252' },
  { hue: '52' },
  { hue: '352' },
  { hue: '152' },
  { hue: '202' },
] as const;

export const THEME_FONT_SIZES = ['10px', '13px', '16px', '19px', '22px'] as const;

export const THEME_BG_OPTIONS: { id: ThemeBg; label: string; cssClass: string }[] = [
  { id: 'light', label: 'Light', cssClass: 'light' },
  { id: 'dim', label: 'Dim', cssClass: 'dim' },
  { id: 'lights-out', label: 'Lights out', cssClass: 'dark' },
];

export function isThemeBg(value: string): value is ThemeBg {
  return value === 'light' || value === 'dim' || value === 'lights-out';
}

export function applyLinkTheme(root: HTMLElement, bg: ThemeBg): void {
  const link = LINK_PRESETS[bg] ?? LINK_PRESETS.light;
  root.style.setProperty('--color-link-lightness', link.link);
  root.style.setProperty('--color-link-hover-lightness', link.hover);
}

export type ThemeCssPatch = {
  dataTheme: ThemeBg;
  colorScheme: 'light' | 'dark';
  fontSize: string;
  properties: Record<string, string>;
};

/** CSS variables + html attrs for a theme. applyTheme writes this onto document.documentElement. */
export function themeCssPatch(state: ThemeState): ThemeCssPatch {
  const bg = BG_PRESETS[state.bg] ?? BG_PRESETS.light;
  const link = LINK_PRESETS[state.bg] ?? LINK_PRESETS.light;
  const secondaryLightness = state.bg === 'light' ? '90%' : '32%';
  const greyLightness = state.bg === 'light' ? '42%' : '68%';
  return {
    dataTheme: state.bg,
    colorScheme: state.bg === 'light' ? 'light' : 'dark',
    fontSize: state.fontSize,
    properties: {
      '--color-primary-hue': state.primaryHue,
      '--color-dark-lightness': bg.dark,
      '--color-light-lightness': bg.light,
      '--color-white-lightness': bg.white,
      '--color-secondary': `hsl(${state.primaryHue}, 55%, ${secondaryLightness})`,
      '--color-link-lightness': link.link,
      '--color-link-hover-lightness': link.hover,
      '--color-grey-lightness': greyLightness,
    },
  };
}

export function parseThemeFromJson(raw: string | null): ThemeState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ThemeState>;
    const bg = parsed.bg && isThemeBg(parsed.bg) ? parsed.bg : THEME_DEFAULT.bg;
    return {
      fontSize: parsed.fontSize || THEME_DEFAULT.fontSize,
      primaryHue: parsed.primaryHue || THEME_DEFAULT.primaryHue,
      bg,
    };
  } catch {
    return null;
  }
}

export function resolveEffectiveTheme(
  userCookieRaw: string | null,
  appearance: SiteAppearancePublic | null,
): ThemeState {
  const userTheme = parseThemeFromJson(userCookieRaw);
  const defaultBg =
    appearance?.defaultBg && isThemeBg(appearance.defaultBg)
      ? appearance.defaultBg
      : THEME_DEFAULT.bg;

  const bg = userTheme?.bg ?? defaultBg;
  const fontSize = userTheme?.fontSize ?? THEME_DEFAULT.fontSize;

  let primaryHue = userTheme?.primaryHue ?? THEME_DEFAULT.primaryHue;
  if (appearance?.accentLocked && appearance.lockedPrimaryHue) {
    primaryHue = appearance.lockedPrimaryHue;
  }

  return { fontSize, primaryHue, bg };
}

export function applyTheme(state: ThemeState): void {
  if (typeof document === 'undefined') return;
  const patch = themeCssPatch(state);
  const root = document.documentElement;
  root.setAttribute('data-theme', patch.dataTheme);
  root.style.colorScheme = patch.colorScheme;
  root.style.fontSize = patch.fontSize;
  for (const [name, value] of Object.entries(patch.properties)) {
    root.style.setProperty(name, value);
  }
}

/** Apply saved theme from cookie merged with site appearance. Returns false if no cookie and no appearance. */
export function applyPersistedThemeFromCookies(appearance?: SiteAppearancePublic | null): boolean {
  const raw = readThemeCookie();
  if (!raw && !appearance) return false;
  applyTheme(resolveEffectiveTheme(raw, appearance ?? null));
  return true;
}
